import { CloseOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import sampleFileAdd from "../../src/data/sample_file_add_bulk_inventory.xlsx";
import sampleFileAdd2 from "../../src/data/sample_file_add_bulk_inventory2.xlsx";
import sampleFileAddLoose from "../../src/data/sample_file_add_bulk_inventory_loose.xlsx";
import sampleFileAddLoose2 from "../../src/data/sample_file_add_bulk_inventory_loose2.xlsx";
import { useDispatch, useSelector } from "react-redux";
import {
  getInventorydashboard,
  getInventoryEditResttriction,
  UploadBulkInventory,
  UploadBulkLooseInventory,
} from "../API  Functions/InventoryFunctions";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { Download, Barcode, Package, FileSpreadsheet, AlertCircle, CheckCircle2 } from "lucide-react";
import { setDashData } from "../Redux/Action/DashData";
import LoadingSpinner from "../components/Loading";
import { useNavigate } from "react-router-dom";

/* ─── Design tokens ───────────────────────────────────────────────────── */
const T = {
  accent:     "#1b4597",
  accentDark: "#153880",
  accentBg:   "#eff4ff",
  accentBd:   "#b3c8f5",
  border:     "#e2e8f0",
  muted:      "#64748b",
  surface:    "#f8fafc",
  text:       "#0f172a",
  sub:        "#475569",
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');

.im * { box-sizing: border-box; font-family: 'DM Sans', sans-serif; margin: 0; padding: 0; }

/* Overlay */
.im-ov {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(15,23,42,0.6);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
}

/* Modal shell */
.im-box {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 32px 80px rgba(15,23,42,0.22);
  width: min(760px, 94vw);
  max-height: 94vh;
  display: flex; flex-direction: column;
  overflow: hidden;
}

/* Header */
.im-hd {
  padding: 16px 22px;
  display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid ${T.border};
  flex-shrink: 0;
}
.im-hd-title { font-size: 16px; font-weight: 700; color: ${T.text}; letter-spacing: -0.2px; }
.im-hd-close {
  width: 28px; height: 28px; border-radius: 7px;
  border: 1px solid ${T.border}; background: transparent;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: ${T.muted}; transition: all .15s;
}
.im-hd-close:hover { background: ${T.surface}; color: ${T.text}; }

/* Tabs */
.im-tabs {
  display: flex; gap: 6px;
  padding: 14px 22px 0;
  flex-shrink: 0;
}
.im-tab {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 9px 10px; border-radius: 10px;
  border: 1.5px solid ${T.border};
  background: ${T.surface};
  font-size: 13px; font-weight: 600; color: ${T.muted};
  cursor: pointer; transition: all .18s;
}
.im-tab:hover:not(.im-tab-on) { border-color: #94a3b8; color: ${T.sub}; background: #f1f5f9; }
.im-tab-on {
  border-color: ${T.accent}; background: ${T.accentBg}; color: ${T.accentDark};
  box-shadow: 0 0 0 3px ${T.accentBd}30;
}

/* Scrollable body */
.im-body {
  padding: 14px 22px 4px;
  overflow-y: auto; flex: 1;
  display: flex; flex-direction: column; gap: 10px;
}

/* Info chip */
.im-chip {
  display: flex; align-items: center; gap: 7px;
  background: ${T.accentBg}; border: 1px solid ${T.accentBd};
  border-radius: 8px; padding: 7px 11px;
  font-size: 12px; color: ${T.accentDark};
}

/* Section label */
.im-lbl {
  font-size: 10.5px; font-weight: 700; letter-spacing: .7px;
  text-transform: uppercase; color: ${T.muted};
  margin-bottom: 6px;
}

/* Option grids */
.im-g3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 6px; }
.im-g2 { display: grid; grid-template-columns: repeat(2,1fr); gap: 6px; }

/* Option pill */
.im-opt {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 10px; border-radius: 9px;
  border: 1.5px solid ${T.border}; background: ${T.surface};
  font-size: 12.5px; font-weight: 500; color: ${T.sub};
  cursor: pointer; transition: all .15s; user-select: none;
}
.im-opt:hover { border-color: #94a3b8; background: #f1f5f9; }
.im-opt-on { border-color: ${T.accent}; background: ${T.accentBg}; color: ${T.accentDark}; }

/* Radio dot */
.im-radio {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: all .15s;
}
.im-opt-on .im-radio { border-color: ${T.accent}; background: ${T.accent}; }
.im-rdot { width: 5px; height: 5px; border-radius: 50%; background: #fff; }

/* Checkbox */
.im-chk {
  width: 14px; height: 14px; border-radius: 4px;
  border: 2px solid #cbd5e1;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  transition: all .15s; font-size: 8px; color: #fff;
}
.im-opt-on .im-chk { border-color: ${T.accent}; background: ${T.accent}; }

/* Divider */
.im-div { height: 1px; background: ${T.border}; }

/* Upload row */
.im-up {
  display: flex; align-items: center; gap: 11px;
  padding: 11px 13px; border-radius: 10px;
  border: 1.5px dashed #cbd5e1; background: ${T.surface};
  transition: border-color .15s;
}
.im-up:hover { border-color: ${T.accent}; }
.im-up-ico {
  width: 36px; height: 36px; border-radius: 8px;
  background: ${T.accentBg};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.im-up-info { flex: 1; min-width: 0; }
.im-up-name { font-size: 13px; font-weight: 600; color: ${T.text}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.im-up-sub { font-size: 11.5px; color: ${T.muted}; margin-top: 1px; }
.im-up-btn {
  padding: 7px 13px; border-radius: 7px;
  background: ${T.text}; color: #fff; border: none;
  font-size: 12px; font-weight: 600; cursor: pointer; white-space: nowrap;
  transition: background .15s;
}
.im-up-btn:hover { background: #1e293b; }

/* Sample btn */
.im-samp {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 11px; border-radius: 7px;
  border: 1px solid ${T.border}; background: #fff;
  font-size: 12px; font-weight: 600; color: ${T.accentDark};
  cursor: pointer; transition: all .15s;
}
.im-samp:hover { background: ${T.accentBg}; border-color: ${T.accent}; }

/* Error text */
.im-err { font-size: 11.5px; color: #dc2626; margin-top: 4px; }

/* Bounce btn */
.im-bounce {
  width: 100%; padding: 9px; border: none; border-radius: 9px;
  background: #fef9c3; color: #854d0e;
  font-size: 12.5px; font-weight: 600; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: background .15s;
}
.im-bounce:hover { background: #fef08a; }

/* Footer */
.im-ft {
  padding: 12px 22px;
  border-top: 1px solid ${T.border};
  display: flex; gap: 8px; flex-shrink: 0;
}
.im-btn {
  flex: 1; padding: 10px 0; border-radius: 10px;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  border: none; transition: all .15s;
}
.im-btn-cancel {
  background: ${T.surface}; color: ${T.sub};
  border: 1.5px solid ${T.border};
}
.im-btn-cancel:hover { background: #f1f5f9; }
.im-btn-submit { background: ${T.accent}; color: #fff; flex: 2; }
.im-btn-submit:hover:not(:disabled) { background: ${T.accentDark}; }
.im-btn-submit:disabled { opacity: .4; cursor: not-allowed; }

/* Success */
.im-ok {
  display: flex; flex-direction: column; align-items: center;
  padding: 48px 32px; text-align: center; gap: 6px;
}
.im-ok h2 { font-size: 20px; font-weight: 700; color: ${T.text}; margin-top: 14px; }
.im-ok p { font-size: 14px; color: ${T.muted}; max-width: 300px; }
.im-ok-btns { display: flex; gap: 10px; margin-top: 22px; }
`;

/* ─── Micro-components ───────────────────────────────────────────────── */
const Opt = ({ label, selected, onChange, checkbox = false }) => (
  <div className={`im-opt${selected ? " im-opt-on" : ""}`} onClick={onChange}>
    {checkbox
      ? <div className="im-chk">{selected && "✓"}</div>
      : <div className="im-radio">{selected && <div className="im-rdot" />}</div>}
    <span>{label}</span>
  </div>
);

/* ─── Main ───────────────────────────────────────────────────────────── */
const ImportInventory = ({ isAdd, isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user_id  = useSelector((s) => s?.login?.user?.result?.user_id);
  const company_id = useSelector((s) => s?.login?.user?.result?.company_id);
  const userData   = useSelector((s) => s?.login?.user?.result);

  /* tab */
  const [tab, setTab] = useState("barcode");

  /* form state */
  const [isPriceChecked, setIsPriceChecked]           = useState(true);
  const [quantityOption, setQuantityOption]           = useState("update");
  const [isUpdatePriceOnly, setIsUpdatePriceOnly]     = useState(false);
  const [isUpdateQtyOnly,   setIsUpdateQtyOnly]       = useState(false);
  const [isCustomName,      setIsCustomName]          = useState(false);
  const [isShortCode,       setIsShortCode]           = useState(false);
  const [isProductGroup,    setIsProductGroup]        = useState(false);
  const [isProductTax,      setIsProductTax]          = useState(false);
  const [isEtaEnable,       setIsEtaEnable]           = useState(null);
  const [isWeighingCode, setIsWeighingCode] = useState(false);
 
  /* file */
  const [base64File, setBase64File]   = useState("");
  const [fileName,   setFileName]     = useState("");
  const [fileError,  setFileError]    = useState("");

  /* async */
  const [loading,        setLoading]        = useState(false);
  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [bounceDetails,  setBounceDetails]  = useState([]);

 const reset = () => {
  setBase64File(""); setFileName(""); setFileError("");
  setBounceDetails([]); setErrorMessage("");
  setIsPriceChecked(true); setIsUpdatePriceOnly(false);
  setIsUpdateQtyOnly(false); setQuantityOption("update");
  setIsWeighingCode(false); // ← add this
};

  useEffect(() => { fetchRestriction(); }, []);

  const fetchRestriction = async () => {
    try {
      const res = await getInventoryEditResttriction(user_id);
      if (res?.result?.status_code === 200) setIsEtaEnable(res?.result?.is_eta_enabled);
    } catch (e) { console.log(e); }
  };

  const refreshKPI = () =>
    getInventorydashboard(user_id, company_id).then((r) =>
      dispatch(setDashData(r?.result[0]?.response)));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setErrorMessage(""); setSuccessMessage("");

    const isBoth = !isUpdatePriceOnly && !isUpdateQtyOnly;
    const isSubOrWH = userData?.is_sub_store || userData?.is_warehouse;

    const payload = {
      user_id, 
      file: base64File,
       file_name: fileName,
      is_update_price:    !isUpdateQtyOnly && isPriceChecked,
      update_price_only:  !isUpdateQtyOnly && isUpdatePriceOnly,
      is_update_qty:      !isUpdateQtyOnly && quantityOption === "update",
      is_add_qty:         isUpdateQtyOnly  || (!isUpdatePriceOnly && quantityOption === "add"),
      is_update_discount: false,
      is_add_product:     true,
      is_custom_name:     isSubOrWH ? false : isAdd ? true : isCustomName,
      is_short_code:      isSubOrWH ? false : isAdd ? true : isShortCode,
      is_product_group:   isSubOrWH ? false : isAdd ? true : isProductGroup,
      ...(tab === "loose" && { is_weighing_code: isWeighingCode }),
      // is_add_tax:         isSubOrWH ? false : isEtaEnable ? isProductTax : false,
    };

    try {
      const fn = tab === "loose" ? UploadBulkLooseInventory : UploadBulkInventory;
      const result = await fn(payload);
      if (result.result.status_code === 200) {
        toast.success(t(result.result.status));
        refreshKPI();
        setSuccessMessage(result?.result?.status);
        setBase64File(""); setFileName("");
      } else if (result.result.bounce_details?.length > 0) {
        setBounceDetails(result.result.bounce_details);
        setBase64File(""); setFileName("");
      } else {
        setErrorMessage(result.message || "An error occurred.");
      }
    } catch { setErrorMessage("Upload failed. Please try again."); }
    finally { setLoading(false); }
  };

  const downloadBounce = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      bounceDetails.map((i) => ({
        Barcode: i.barcode, Quantity: i.onhand_qty,
        "Sales Price": i.sale_price, "Cost Price": i.cost_price,
        "Warning": i.message,
      }))
    ), "Errors");
    XLSX.writeFile(wb, "bounce_details.xlsx");
  };

const downloadSample = () => {
  const link = document.createElement("a");
  if (tab === "loose") {
    if (userData?.is_sub_store || userData?.is_warehouse) {
      link.href = sampleFileAddLoose2;
      link.download = "sample_file_add_bulk_inventory_loose2.xlsx";
    } else {
      link.href = sampleFileAddLoose;
      link.download = "sample_file_add_bulk_inventory_loose.xlsx";
    }
  } else if (userData?.is_sub_store || userData?.is_warehouse) {
    link.href = sampleFileAdd2;
    link.download = "sample_file_add_bulk_inventory2.xlsx";
  } else {
    link.href = sampleFileAdd;
    link.download = "sample_file_add_bulk_inventory.xlsx";
  }
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
};

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xls","xlsx"].includes(ext)) {
      setFileError("Please upload a valid Excel file (.xls or .xlsx)");
      setBase64File(""); setFileName(""); return;
    }
    setFileError(""); setFileName(file.name);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => setBase64File(reader.result.split(",")[1]);
  };

  const isBoth    = !isUpdatePriceOnly && !isUpdateQtyOnly;
  const showExtra = !userData?.is_sub_store && !userData?.is_warehouse;

  if (!isOpen) return null;

  return (
    <>
      <style>{css}</style>
      {loading && <LoadingSpinner />}

      <div className="im im-ov" onClick={onClose}>
        <div className="im-box" onClick={(e) => e.stopPropagation()}>

          {/* ── Success ──────────────────────────────────── */}
          {successMessage ? (
            <div className="im-ok">
              <CheckCircle2 size={52} color={T.accent} strokeWidth={1.5} />
              <h2>{t("Upload Successful")}</h2>
              <p>{t(successMessage)}</p>
              <div className="im-ok-btns">
                <button className="im-btn im-btn-cancel"
                  style={{ padding: "10px 28px" }}
                  onClick={() => { setSuccessMessage(""); onClose(); }}>
                  {t("Close")}
                </button>
                <button className="im-btn im-btn-submit"
                  style={{ padding: "10px 28px" }}
                  onClick={() => { setSuccessMessage(""); onClose(); navigate("/settings/bulkupdatehistory"); }}>
                  {t("Check Status")}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* ── Header ─────────────────────────────────── */}
              <div className="im-hd">
                <span className="im-hd-title">
                  {isAdd ? t("Add Products") : t("Update Inventory")}
                </span>
                <button className="im-hd-close" onClick={onClose}>
                  <CloseOutlined style={{ fontSize: 11 }} />
                </button>
              </div>

              {/* ── Tabs ───────────────────────────────────── */}
              <div className="im-tabs">
                {[
                  { id: "barcode", Icon: Barcode,  label: t("Barcode Products") },
                  { id: "loose",   Icon: Package,  label: t("Loose Items") },
                ].map(({ id, Icon, label }) => (
                  <button key={id} type="button"
                    className={`im-tab${tab === id ? " im-tab-on" : ""}`}
                    onClick={() => { setTab(id); reset(); }}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>

              {/* ── Body ───────────────────────────────────── */}
              <div className="im-body">
                {/* Info chip */}
                <div className="im-chip">
                  <AlertCircle size={13} />
                  {tab === "barcode"
                    ? t("Each row requires a valid barcode.")
                    : t("No barcode needed — for weight-based or unpackaged items.")}
                </div>

                <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 10 }}>

                  {/* Update options — hidden in isAdd mode */}
                  {!isAdd && (
                    <>
                      <div>
                        <div className="im-lbl">{t("Update Type")}</div>
                        <div className="im-g3">
                          <Opt label={t("Price & Quantity")} selected={isBoth}
                            onChange={() => { setIsPriceChecked(true); setIsUpdatePriceOnly(false); setIsUpdateQtyOnly(false); }} />
                          <Opt label={t("Price Only")} selected={isUpdatePriceOnly}
                            onChange={() => { setIsUpdatePriceOnly(true); setIsPriceChecked(false); setIsUpdateQtyOnly(false); }} />
                          <Opt label={t("Quantity Only")} selected={isUpdateQtyOnly}
                            onChange={() => { setIsUpdateQtyOnly(true); setIsPriceChecked(false); setIsUpdatePriceOnly(false); setQuantityOption("update"); }} />
                        </div>
                      </div>

                      {isBoth && (
                        <div>
                          <div className="im-lbl">{t("Quantity Mode")}</div>
                          <div className="im-g2">
                            <Opt label={t("Update (replace)")} selected={quantityOption === "update"}
                              onChange={() => setQuantityOption("update")} />
                            <Opt label={t("Add (stack)")} selected={quantityOption === "add"}
                              onChange={() => setQuantityOption("add")} />
                          </div>
                        </div>
                      )}

                   {isBoth && (showExtra || tab === "loose") && (
  <div>
    <div className="im-lbl">{t("Optional Columns")}</div>
    <div className="im-g3">
      {showExtra && (
        <>
          <Opt label={t("Custom Name")}    selected={isCustomName}   onChange={() => setIsCustomName(!isCustomName)}     checkbox />
          <Opt label={t("Short Code")}     selected={isShortCode}    onChange={() => setIsShortCode(!isShortCode)}         checkbox />
          <Opt label={t("Product Group")}  selected={isProductGroup} onChange={() => setIsProductGroup(!isProductGroup)}   checkbox />
          {isEtaEnable && (
            <Opt label={t("Product Taxes")} selected={isProductTax} onChange={() => setIsProductTax(!isProductTax)} checkbox />
          )}
        </>
      )}
      {tab === "loose" && (
        <Opt
          label={t("Weighing Scale Product Code")}
          selected={isWeighingCode}
          onChange={() => setIsWeighingCode(!isWeighingCode)}
          checkbox
        />
      )}
    </div>
  </div>
)}

                      <div className="im-div" />
                    </>
                  )}

                  {/* Sample download */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12.5, color: T.muted }}>{t("Need a template?")}</span>
                    <button type="button" className="im-samp" onClick={downloadSample}>
                      <Download size={12} /> {t("Download Sample")}
                    </button>
                  </div>

                  {/* Upload zone */}
                  <div className="im-up">
                    <div className="im-up-ico">
                      <FileSpreadsheet size={17} color={T.accent} />
                    </div>
                    <div className="im-up-info">
                      <div className="im-up-name">{fileName || t("No file chosen")}</div>
                      <div className="im-up-sub">
                        {fileName ? t("Ready to submit") : t("Accepts .xls and .xlsx")}
                      </div>
                    </div>
                    <input type="file" id="im-file" style={{ display: "none" }}
                      accept=".xlsx,.xls" onChange={handleFile} />
                    <button type="button" className="im-up-btn"
                      onClick={() => document.getElementById("im-file").click()}>
                      {fileName ? t("Change") : t("Browse")}
                    </button>
                  </div>

                  {fileError   && <p className="im-err">{fileError}</p>}
                  {errorMessage && <p className="im-err">{errorMessage}</p>}

                  {bounceDetails.length > 0 && (
                    <button type="button" className="im-bounce" onClick={downloadBounce}>
                      <AlertCircle size={13} />
                      {t("Download Error Report")} — {bounceDetails.length} {t("rows failed")}
                    </button>
                  )}
                </form>
              </div>

              {/* ── Footer ─────────────────────────────────── */}
              <div className="im-ft">
                <button type="button" className="im-btn im-btn-cancel" onClick={onClose}>
                  {t("Cancel")}
                </button>
                <button type="submit" className="im-btn im-btn-submit"
                  disabled={!base64File || loading}
                  onClick={handleSubmit}>
                  {loading ? t("Uploading…") : t("Upload File")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ImportInventory;


// import { CloseOutlined } from "@ant-design/icons";
// import React, { useEffect, useState } from "react";
// import { useTranslation } from "react-i18next";
// import sampleFileAdd from "../../src/data/sample_file_add_bulk_inventory.xlsx";
// import sampleFileAdd2 from "../../src/data/sample_file_add_bulk_inventory2.xlsx";
// import download_icon from "../../src/assets/download_icon.svg";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   getInventorydashboard,
//   getInventoryEditResttriction,
//   UploadBulkInventory,
// } from "../API  Functions/InventoryFunctions";
// import * as XLSX from "xlsx";
// import { toast } from "react-toastify";
// import { Download } from "lucide-react";
// import { message, Upload } from "antd";
// import { setDashData } from "../Redux/Action/DashData";
// import LoadingSpinner from "../components/Loading";
// import checked from "../../src/assets/checked.svg";
// import { useNavigate } from "react-router-dom";

// const ImportInventory = ({ isAdd, isOpen, onClose }) => {
//   const dispatch = useDispatch();
//   const { t, i18n } = useTranslation();
//   const user_id = useSelector((state) => state?.login?.user?.result?.user_id);
//   const navigate = useNavigate();

//   const [isPriceChecked, setIsPriceChecked] = useState(false);
//   const [quantityOption, setQuantityOption] = useState("update");
//   const [fileError, setFileError] = useState("");
//   const [base64File, setBase64File] = useState("");
//   const [fileName, setFileName] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");
//   const [successMessage, setSuccessMessage] = useState("");
//   const [isUpdatePriceOnlyChecked, setIsUpdatePriceOnlyChecked] =
//     useState(false);
//   const [isDiscuntedPrice, setIsDiscountedPrice] = useState(false);
//   const [isUpdateQtyOnlyChecked, setIsUpdateQtyOnlyChecked] = useState(false);
//   const [bounceDetails, setBounceDetails] = useState([]); // State to store bounce details
//   const [isCustomProductName, setIsCustomProductName] = useState(false);
//   const [isShortCode, setIsShortCode] = useState(false);
//   const [isUoM, setIsUoM] = useState(false);
//   const [isWeighingCode, setIsWeighingCode] = useState(false);
//   const [isProductGroup, setIsProductGroup] = useState(false);
//   const [isProductTexes, setIsProductTexes] = useState(false);
//   const userData = useSelector((state) => state?.login?.user?.result);
//   const [isEtaEnable, setIsEtaEnable] = useState(null);

//   const styles = {
//     overlay: {
//       position: "fixed",
//       top: 0,
//       left: 0,
//       width: "100%",
//       height: "100%",
//       backgroundColor: "rgba(0, 0, 0, 0.5)",
//       zIndex: 50,
//     },
//     container: {
//       width: isAdd ? "40%" : "70%",
//       padding: successMessage == "" ? "30px" : "15px",
//       borderRadius: "18px",
//       margin: "0 auto",
//       backgroundColor: "#ffffff",
//       boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//       position: "fixed",
//       top: "50%",
//       left: "50%",
//       transform: "translate(-50%, -50%)",
//       zIndex: 50,
//     },
//     header: {
//       display: "flex",
//       justifyContent: "space-between",
//       alignItems: "center",
//       marginBottom: 5,
//     },
//     title: {
//       fontSize: "20px",
//       fontWeight: "bold",
//       textAlign: "center",
//       marginBottom: "10px",
//     },
//     cancelIcon: {
//       cursor: "pointer",
//     },
//     description: {
//       textAlign: "center",
//       fontSize: "16px",
//       color: "#272728",
//       marginTop: "10px",
//     },
//     input: {
//       width: "100%",
//       padding: "10px",
//       borderRadius: "12px",
//       border: "1px solid #CCCCCC",
//       marginBottom: "20px",
//       boxSizing: "border-box",
//     },
//     buttonContainer: {
//       display: "flex",
//       justifyContent: "center",
//     },
//     button: {
//       width: "180px",
//       height: "45px",
//       borderRadius: "5px",
//       border: "none",
//       cursor: "pointer",
//       marginRight: "10px",
//       transition: "opacity 0.3s", // Smooth transition for opacity change
//     },
//     confirmButton: {
//       backgroundColor: "primaryBlue",
//       color: "#FFFFFF",
//     },
//     cancelButton: {
//       backgroundColor: "#CCCCCC",
//       color: "#000000",
//     },
//     image: {
//       width: "70px",
//       display: "block",
//       margin: "20px auto",
//       marginBottom: "20px",
//     },
//   };

//   const company_id = useSelector(
//     (state) => state?.login?.user?.result?.company_id
//   );

//   useEffect(()=>{
//     CheckUserAcessForInventory();
//   },[])

//   const getInventoryKPI = () => {
//     getInventorydashboard(user_id, company_id).then((res) => {
//       dispatch(setDashData(res?.result[0]?.response));
//       // console.log("INVENTORY DASHBOARD DATA", res?.result[0]?.response);
//     });
//   };

//     const CheckUserAcessForInventory = async () => {
//         try {
//           const res = await getInventoryEditResttriction(user_id);
//           if (res?.result?.status_code == 200) {
//             setIsEtaEnable(res?.result?.is_eta_enabled);
//           }
//         } catch (error) {
//           console.log(error);
//         } finally {
//         }
//       };

//   const isSubmitDisabled =
//     loading ||
//     !base64File ||
//     (!isPriceChecked && !isUpdatePriceOnlyChecked && !isUpdateQtyOnlyChecked);
//   // Handle form submission
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     setErrorMessage("");
//     setSuccessMessage("");

//     const payload = {
//       user_id: user_id,
//       file: base64File,
//       file_name: fileName,
//       is_update_price: !isUpdateQtyOnlyChecked && isPriceChecked,
//       update_price_only: !isUpdateQtyOnlyChecked && isUpdatePriceOnlyChecked,
//       is_update_qty: !isUpdateQtyOnlyChecked && quantityOption === "update",
//       is_add_qty:
//         isUpdateQtyOnlyChecked ||
//         (!isUpdatePriceOnlyChecked && quantityOption === "add"),
//       is_update_discount: isDiscuntedPrice,
//       is_add_product: true,
//       is_custom_name: (userData?.is_sub_store || userData?.is_warehouse) ? false : isAdd ? true : isCustomProductName,
//       is_short_code: (userData?.is_sub_store || userData?.is_warehouse) ? false :  isAdd ? true : isShortCode,
//       is_product_group: (userData?.is_sub_store || userData?.is_warehouse) ? false :  isAdd ? true : isProductGroup,
//       is_add_tax :(userData?.is_sub_store || userData?.is_warehouse) ? false : isEtaEnable ? isProductTexes : false,
//       // "is_uom":isAdd ? true : isUoM,
//       // "is_weighing_code":isAdd ? true : isWeighingCode
//     };

//     try {
//       const result = await UploadBulkInventory(payload);

//       // console.log("result", result.result.bounce_details.length );
//       // console.log("bpunce result", result.result.bounce_details );
//       // if (
//       //   result.result.status_code === 200 &&
//       //   result.result.bounce_details.length > 0 &&
//       //   result.result.is_updated === true
//       // ) {
//       //   toast.error(
//       //     t(
//       //       "Some data is updated some is invalid. Download the Excel file for invalid data."
//       //     )
//       //   );
//       // }

//       if (result.result.status_code === 200) {
//         toast.success(t(result.result.status));
//         getInventoryKPI();
//         setSuccessMessage(result?.result?.status);
//         // onClose();
//         setBase64File(""); // Clear the base64 file
//         setFileName(""); // Clear the file name
//       } else if (result.result.bounce_details.length > 0) {
//         console.log("RESSS", result.result.bounce_details);

//         setBounceDetails(result.result.bounce_details);
//         setBase64File(""); // Clear the base64 file
//         setFileName(""); // Clear the file name
//         // }
//       } else {
//         setErrorMessage(
//           result.message || "An error occurred during file upload."
//         );
//       }
//     } catch (error) {
//       setErrorMessage("Failed to upload file. Please try again.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   // console.log("BOUNCE DETAILS", bounceDetails);

//   const downloadBounceExcel = () => {
//     const wb = XLSX.utils.book_new();
//     const ws = XLSX.utils.json_to_sheet(
//       bounceDetails.map((item) => ({
//         Barcode: item.barcode,
//         Quantity: item.onhand_qty,
//         "Sales Price": item.sale_price,
//         "Cost Price": item.cost_price,
//         "Warning Message": item.message,
//       }))
//     );

//     XLSX.utils.book_append_sheet(wb, ws, "Bounce Details");

//     // Generate a file and trigger download
//     XLSX.writeFile(wb, "bounce_details.xlsx");
//   };

//   useEffect(() => {
//     setIsPriceChecked(true);
//   }, []);

//   // Function to trigger file download
//   const downloadSampleFile = () => {
//     const link = document.createElement("a");

//     if (userData?.is_sub_store || userData?.is_warehouse) {
//       link.href = sampleFileAdd2; // use second file
//       link.download = "sample_file_add_bulk_inventory2.xlsx";
//     } else {
//       link.href = sampleFileAdd; // use first file
//       link.download = "sample_file_add_bulk_inventory.xlsx";
//     }

//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//   };

//   // Function to handle file upload
//   const handleFileUpload = (e) => {
//     const file = e.target.files[0];
//     if (file) {
//       const fileExtension = file.name.split(".").pop().toLowerCase();
//       if (fileExtension !== "xls" && fileExtension !== "xlsx") {
//         setFileError("Please upload a valid Excel file.");
//         setBase64File("");
//         setFileName(""); // Clear file name if the file type is invalid
//       } else {
//         setFileError("");
//         setFileName(file.name); // Store the file name
//         convertToBase64(file);
//       }
//     }
//   };

//   const convertToBase64 = (file) => {
//     const reader = new FileReader();
//     reader.readAsDataURL(file);
//     reader.onloadend = () => {
//       const base64Data = reader.result.split(",")[1];
//       setBase64File(base64Data);
//     };
//   };

//   // console.log("base64File", base64File);

//   // const isSubmitDisabled = loading || !base64File;

//   return (
//     <>
//       {loading && <LoadingSpinner />}

//       {isOpen && (
//         <>
//           {successMessage != "" ? (
//             <div>
//               <div style={styles.overlay}></div>
//               <div style={styles.container}>
//                 <img
//                   // src={checked}
//                   src={checked}
//                   alt="Checked"
//                   style={styles.image}
//                 />
//                 {/* <p style={styles.title}>{t('successfull')}</p> */}
//                 <p style={styles.title}>{t("Successful")}</p>
//                 <p style={styles.description}>{t(successMessage)}</p>
//                 <div className="flex justify-center gap-5 mt-7 mb-7 w-full">
//                   {/* <Link to={route}> */}
//                   <button
//                     type="button"
//                     className="w-[180px] h-[45px] bg-white text-primaryBlue border border-primaryBlue rounded-lg font-semibold"
//                     // onClick={handleGoToDashboard} // Call handleGoToDashboard onClick
//                     onClick={() => {
//                       setSuccessMessage("");
//                       onClose();
//                     }}
//                   >
//                     {t("Close")}
//                   </button>
//                   <button
//                     type="button"
//                     className="w-[180px] h-[45px] bg-primaryBlue text-white rounded-lg font-semibold"
//                     // onClick={handleGoToDashboard} // Call handleGoToDashboard onClick
//                     onClick={() => {
//                       setSuccessMessage("");
//                       onClose();
//                       navigate("/settings/bulkupdatehistory");
//                     }}
//                   >
//                     {t("Check Status")}
//                   </button>
//                   {/* </Link> */}
//                 </div>
//               </div>
//             </div>
//           ) : (
//             <>
//               <div
//                 className="backdrop-blur"
//                 style={styles.overlay}
//                 onClick={onClose}
//               ></div>
//               <div style={styles.container}>
//                 <div style={styles.header}>
//                   <p style={styles.title}>
//                     {isAdd ? t("Add Products") : t("Update Inventory")}
//                   </p>
//                   <CloseOutlined style={styles.cancelIcon} onClick={onClose} />
//                 </div>
//                 <form onSubmit={handleSubmit} className="space-y-6">
//                   {/* Update Options */}
//                   {!isAdd && (
//                     <>
//                       <div className="grid grid-cols-3 gap-4">
//                         {/* <label className="relative bg-gray-50 rounded-lg p-4 cursor-pointer"> */}
//                         <label
//                           className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
//                             !isUpdatePriceOnlyChecked && !isUpdateQtyOnlyChecked
//                               ? "bg-gray-200 text-black"
//                               : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//                           }`}
//                         >
//                           <input
//                             type="radio"
//                             name="updateType"
//                             checked={
//                               !isUpdatePriceOnlyChecked &&
//                               !isUpdateQtyOnlyChecked
//                             }
//                             onChange={() => {
//                               setIsPriceChecked(true);
//                               setIsUpdatePriceOnlyChecked(false);
//                               setIsUpdateQtyOnlyChecked(false);
//                             }}
//                             className="sr-only"
//                           />
//                           <div
//                             className={`flex items-center gap-2 ${
//                               !isUpdatePriceOnlyChecked &&
//                               !isUpdateQtyOnlyChecked
//                                 ? "text-blue-600"
//                                 : "text-gray-700"
//                             }`}
//                           >
//                             <div
//                               className={`w-4 h-4 rounded-full border-2 ${
//                                 !isUpdatePriceOnlyChecked &&
//                                 !isUpdateQtyOnlyChecked
//                                   ? "border-blue-500 bg-blue-500"
//                                   : "border-gray-300"
//                               }`}
//                             >
//                               {!isUpdatePriceOnlyChecked &&
//                                 !isUpdateQtyOnlyChecked && (
//                                   <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                 )}
//                             </div>
//                             <span className="text-sm">
//                               {t("Update Price & Quantity")}
//                             </span>
//                           </div>
//                         </label>

//                         {/* <label className="relative bg-gray-50 rounded-lg p-4 cursor-pointer"> */}
//                         <label
//                           className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
//                             isUpdatePriceOnlyChecked
//                               ? "bg-gray-200 text-black"
//                               : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//                           }`}
//                         >
//                           <input
//                             type="radio"
//                             name="updateType"
//                             checked={isUpdatePriceOnlyChecked}
//                             onChange={() => {
//                               setIsUpdatePriceOnlyChecked(true);
//                               setIsPriceChecked(false);
//                               setIsUpdateQtyOnlyChecked(false);
//                             }}
//                             className="sr-only"
//                           />
//                           <div
//                             className={`flex items-center gap-2 ${
//                               isUpdatePriceOnlyChecked
//                                 ? "text-blue-600"
//                                 : "text-gray-700"
//                             }`}
//                           >
//                             <div
//                               className={`w-4 h-4 rounded-full border-2 ${
//                                 isUpdatePriceOnlyChecked
//                                   ? "border-blue-500 bg-blue-500"
//                                   : "border-gray-300"
//                               }`}
//                             >
//                               {isUpdatePriceOnlyChecked && (
//                                 <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                               )}
//                             </div>
//                             <span className="text-sm">
//                               {t("Update Price Only")}
//                             </span>
//                           </div>
//                         </label>

//                         {/* <label className="relative bg-gray-50 rounded-lg p-4 cursor-pointer"> */}
//                         <label
//                           className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
//                             isUpdateQtyOnlyChecked
//                               ? "bg-gray-200 text-black"
//                               : "bg-gray-50  border-2 border-gray-500 hover:bg-gray-100"
//                           }`}
//                         >
//                           <input
//                             type="radio"
//                             name="updateType"
//                             checked={isUpdateQtyOnlyChecked}
//                             onChange={() => {
//                               setIsUpdateQtyOnlyChecked(true);
//                               setIsPriceChecked(false);
//                               setIsDiscountedPrice(false);
//                               setIsUpdatePriceOnlyChecked(false);
//                               setQuantityOption("update");
//                             }}
//                             className="sr-only"
//                           />
//                           <div
//                             className={`flex items-center gap-2 ${
//                               isUpdateQtyOnlyChecked
//                                 ? "text-blue-600"
//                                 : "text-gray-700"
//                             }`}
//                           >
//                             <div
//                               className={`w-4 h-4 rounded-full border-2 ${
//                                 isUpdateQtyOnlyChecked
//                                   ? "border-blue-500 bg-blue-500"
//                                   : "border-gray-300"
//                               }`}
//                             >
//                               {isUpdateQtyOnlyChecked && (
//                                 <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                               )}
//                             </div>
//                             <span className="text-sm">
//                               {t("Update Quantity Only")}
//                             </span>
//                           </div>
//                         </label>
//                         {/* {!isUpdateQtyOnlyChecked && 
//   <label
//     className={`relative rounded-lg p-4 border-2 border-gray-500 cursor-pointer transition-colors duration-200 ${
//       isDiscuntedPrice
//         ? "bg-gray-200 text-black"
//         : "bg-gray-50  hover:bg-gray-100"
//     }`}
//   >
//     <input
//       type="checkbox"
//       name="updateType"
//       checked={isDiscuntedPrice}
//       onChange={(e) => setIsDiscountedPrice(e.target.checked)}
//       className="sr-only"
//     />
//     <div
//       className={`flex items-center gap-2 ${
//         isDiscuntedPrice ? "text-blue-600" : "text-gray-700"
//       }`}
//     >
//       <div
//         className={`w-4 h-4 rounded-full border-2 ${
//           isDiscuntedPrice
//             ? "border-blue-500 bg-blue-500"
//             : "border-gray-300"
//         }`}
//       >
//         {isDiscuntedPrice && (
//           <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//         )}
//       </div>
//       <span className="text-sm">{t("Update Discounted Price")}</span>
//     </div>
//   </label>
// } */}
//                       </div>

//                       {/* Quantity Options */}
//                       {/* {!isUpdatePriceOnlyChecked && ( */}
//                       {!isUpdatePriceOnlyChecked && !isUpdateQtyOnlyChecked && (
//                         <div className="space-y-2 mt-4">
//                           <h3 className="font-medium mb-2">{t("Quantity")}</h3>
//                           <div className="grid grid-cols-3 gap-4">
//                             {/* <label className="relative bg-gray-50 rounded-lg p-4 cursor-pointer"> */}

//                                <label
//                               className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
//                                 quantityOption === "update"
//                                   ? "bg-gray-200 text-black"
//                                   : "bg-gray-50  border-2 border-gray-500 hover:bg-gray-100"
//                               }`}
//                             >
//                               <input
//                                 type="radio"
//                                 name="quantityOption"
//                                 value="update"
//                                 checked={quantityOption === "update"}
//                                 onChange={(e) =>
//                                   setQuantityOption(e.target.value)
//                                 }
//                                 className="sr-only"
//                               />
//                               <div
//                                 className={`flex items-center gap-2 ${
//                                   quantityOption === "update"
//                                     ? "text-blue-600"
//                                     : "text-gray-700"
//                                 }`}
//                               >
//                                 <div
//                                   className={`w-4 h-4 rounded-full border-2 ${
//                                     quantityOption === "update"
//                                       ? "border-blue-500 bg-blue-500"
//                                       : "border-gray-300"
//                                   }`}
//                                 >
//                                   {quantityOption === "update" && (
//                                     <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                   )}
//                                 </div>
//                                 <span className="text-sm">
//                                   {t("Update Quantity")}
//                                 </span>
//                               </div>
//                             </label>

//                             <label
//                               className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 ${
//                                 quantityOption === "add"
//                                   ? "bg-gray-200 text-black"
//                                   : "bg-gray-50  border-2 border-gray-500 hover:bg-gray-100"
//                               }`}
//                             >
//                               <input
//                                 type="radio"
//                                 name="quantityOption"
//                                 value="add"
//                                 checked={quantityOption === "add"}
//                                 onChange={(e) =>
//                                   setQuantityOption(e.target.value)
//                                 }
//                                 className="sr-only"
//                               />
//                               <div
//                                 className={`flex items-center gap-2 ${
//                                   quantityOption === "add"
//                                     ? "text-blue-600"
//                                     : "text-gray-700"
//                                 }`}
//                               >
//                                 <div
//                                   className={`w-4 h-4 rounded-full border-2 ${
//                                     quantityOption === "add"
//                                       ? "border-blue-500 bg-blue-500"
//                                       : "border-gray-300"
//                                   }`}
//                                 >
//                                   {quantityOption === "add" && (
//                                     <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                   )}
//                                 </div>
//                                 <span className="text-sm">
//                                   {t("Add Quantity")}
//                                 </span>
//                               </div>
//                             </label>

//                             {/* <label className="relative bg-gray-50 rounded-lg p-4 cursor-pointer"> */}
                         
//                           </div>
//                        {!userData?.is_sub_store && !userData?.is_warehouse && <>
//                         <div className="space-y-2 mt-4">
//                             <div className="grid grid-cols-3 gap-4 mt-10">
//                               <label
//                                 className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${
//         isCustomProductName
//           ? "bg-gray-200 text-black"
//           : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//       }
//     `}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={isCustomProductName}
//                                   onChange={(e) =>
//                                     setIsCustomProductName(e.target.checked)
//                                   }
//                                   className="sr-only"
//                                 />
//                                 <div className="flex items-center gap-2 text-gray-700">
//                                   <div
//                                     className={`w-4 h-4 rounded-full border-2 ${
//                                       isCustomProductName
//                                         ? "border-blue-500 bg-blue-500"
//                                         : "border-gray-300"
//                                     }`}
//                                   >
//                                     {isCustomProductName && (
//                                       <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                     )}
//                                   </div>
//                                   <span className="text-sm">
//                                     {t("Custom Product Name")}
//                                   </span>
//                                 </div>
//                               </label>
//                               <label
//                                 className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${
//         isShortCode
//           ? "bg-gray-200 text-black"
//           : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//       }
//     `}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={isShortCode}
//                                   onChange={(e) =>
//                                     setIsShortCode(e.target.checked)
//                                   }
//                                   className="sr-only"
//                                 />
//                                 <div className="flex items-center gap-2 text-gray-700">
//                                   <div
//                                     className={`w-4 h-4 rounded-full border-2 ${
//                                       isShortCode
//                                         ? "border-blue-500 bg-blue-500"
//                                         : "border-gray-300"
//                                     }`}
//                                   >
//                                     {isShortCode && (
//                                       <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                     )}
//                                   </div>
//                                   <span className="text-sm">
//                                     {t("Product Short Code")}
//                                   </span>
//                                 </div>
//                               </label>
//                               <label
//                                 className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${
//         isProductGroup
//           ? "bg-gray-200 text-black"
//           : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//       }
//     `}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={isProductGroup}
//                                   onChange={(e) =>
//                                     setIsProductGroup(e.target.checked)
//                                   }
//                                   className="sr-only"
//                                 />
//                                 <div className="flex items-center gap-2 text-gray-700">
//                                   <div
//                                     className={`w-4 h-4 rounded-full border-2 ${
//                                       isProductGroup
//                                         ? "border-blue-500 bg-blue-500"
//                                         : "border-gray-300"
//                                     }`}
//                                   >
//                                     {isProductGroup && (
//                                       <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                     )}
//                                   </div>
//                                   <span className="text-sm">
//                                     {t("Product Group")}
//                                   </span>
//                                 </div>
//                               </label>
//                               {isEtaEnable && 
//                               <label
//                                 className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${
//         isProductGroup
//           ? "bg-gray-200 text-black"
//           : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"
//       }
//     `}
//                               >
//                                 <input
//                                   type="checkbox"
//                                   checked={isProductTexes}
//                                   onChange={(e) =>
//                                     setIsProductTexes(e.target.checked)
//                                   }
//                                   className="sr-only"
//                                 />
//                                 <div className="flex items-center gap-2 text-gray-700">
//                                   <div
//                                     className={`w-4 h-4 rounded-full border-2 ${
//                                       isProductTexes
//                                         ? "border-blue-500 bg-blue-500"
//                                         : "border-gray-300"
//                                     }`}
//                                   >
//                                     {isProductTexes && (
//                                       <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                     )}
//                                   </div>
//                                   <span className="text-sm">
//                                     {t("Product Taxes")}
//                                   </span>
//                                 </div>
//                               </label>
//                               }
                                
//                               {/* <label
//                               className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${isUoM ? "bg-gray-200 text-black" : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"}
//     `}
//                             >
//                               <input
//                                 type="checkbox"
//                                 checked={isUoM}
//                                 onChange={(e) => setIsUoM(e.target.checked)}
//                                 className="sr-only"
//                               />
//                               <div
//                                 className="flex items-center gap-2 text-gray-700"
//                               >
//                                 <div
//                                   className={`w-4 h-4 rounded-full border-2 ${isUoM
//                                     ? "border-blue-500 bg-blue-500"
//                                     : "border-gray-300"
//                                     }`}
//                                 >
//                                   {isUoM && (
//                                     <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                   )}
//                                 </div>
//                                 <span className="text-sm">{t("UoM")}</span>
//                               </div>
//                             </label>
//                             <label
//                               className={`relative rounded-lg p-4 cursor-pointer transition-colors duration-200 
//       ${isWeighingCode ? "bg-gray-200 text-black" : "bg-gray-50 border-2 border-gray-500 hover:bg-gray-100"}
//     `}
//                             >
//                               <input
//                                 type="checkbox"
//                                 checked={isWeighingCode}
//                                 onChange={(e) => setIsWeighingCode(e.target.checked)}
//                                 className="sr-only"
//                               />
//                               <div
//                                 className="flex items-center gap-2 text-gray-700"
//                               >
//                                 <div
//                                   className={`w-4 h-4 rounded-full border-2 ${isWeighingCode
//                                     ? "border-blue-500 bg-blue-500"
//                                     : "border-gray-300"
//                                     }`}
//                                 >
//                                   {isWeighingCode && (
//                                     <div className="w-2 h-2 bg-blue-500 rounded-full m-0.5" />
//                                   )}
//                                 </div>
//                                 <span className="text-sm">{t("Weighing Scale Product Code")}</span>
//                               </div>
//                             </label> */}
//                             </div>
//                           </div>
                       
//                        </>}
//                           {/* Custom Product Name Option */}
                         
//                         </div>
//                       )}
//                     </>
//                   )}

//                   {/* Download Sample */}
//                   <div className="flex items-center gap-2 text-black mt-5">
//                     <span>{t("Download Sample File")}:</span>
//                     <button
//                       type="button"
//                       className="text-blue-600 hover:text-blue-700"
//                       onClick={downloadSampleFile}
//                     >
//                       <Download size={20} />
//                     </button>
//                   </div>

//                   {/* File Upload */}
//                   <div className="space-y-2 mt-4">
//                     <h3 className="font-medium mb-2">{t("Upload File")}</h3>
//                     <div className="border-2 border-dashed border-gray-300  bg-gray-100 rounded-lg p-3">
//                       <div className="flex flex-col items-center">
//                         <Upload className="w-5 h-5 text-gray-400 mb-2" />
//                         {/* <p className="text-sm text-gray-500 mb-1"></p> */}
//                         <input
//                           type="file"
//                           id="file-upload"
//                           className="hidden"
//                           onChange={handleFileUpload}
//                           accept=".xlsx,.xls"
//                         />
//                         <button
//                           type="button"
//                           onClick={() =>
//                             document.getElementById("file-upload").click()
//                           }
//                           className="bg-black text-white text-xs px-4 py-2 rounded-full mb-5"
//                         >
//                           {t("Choose Excel File")}
//                         </button>
//                       </div>
//                     </div>
//                     {fileName && (
//                       <p className="text-sm text-gray-600 mt-2">
//                         {t("Selected file")}: {fileName}
//                       </p>
//                     )}
//                     {fileError && (
//                       <p className="text-sm text-red-600">{fileError}</p>
//                     )}
//                   </div>

//                   {bounceDetails.length > 0 && (
//                     <div className="mt-5">
//                       <button
//                         type="button"
//                         className="w-full h-10 bg-[#78CFEC] text-white font-medium rounded-lg"
//                         onClick={downloadBounceExcel}
//                       >
//                         {t("Download Error File")}
//                       </button>
//                     </div>
//                   )}

//                   {/* Action Buttons */}
//                   <div className="grid grid-cols-3 gap-4 pt-4">
//                     <button
//                       type="button"
//                       onClick={onClose}
//                       className="w-full px-4 py-2 border border-gray-300 font-semibold rounded-lg text-gray-700 hover:bg-gray-50"
//                     >
//                       {t("Cancel")}
//                     </button>
//                     <button
//                       type="submit"
//                       disabled={!base64File || loading}
//                       className={`w-full px-4 py-2 rounded-lg font-semibold text-white ${
//                         loading || !base64File
//                           ? "bg-primaryBlue opacity-50 cursor-not-allowed"
//                           : "bg-primaryBlue hover:bg-gray-600"
//                       }`}
//                       // className="w-full px-4 py-2 bg-primaryBlue text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
//                     >
//                       {t("Submit")}
//                     </button>
//                   </div>
//                 </form>
//               </div>
//             </>
//           )}
//         </>
//       )}
//     </>
//   );
// };

// export default ImportInventory;
